import { useEffect, useState } from 'react';

interface GoodsModalProps {
  selectedImage: string | null;
  imageType?: 'venue' | 'benefit' | 'goods';
  onClose: () => void;
}

export default function GoodsModal({ selectedImage, imageType = 'goods', onClose }: GoodsModalProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (selectedImage) {
      document.body.style.overflow = 'hidden';
      setIsVisible(true);
      requestAnimationFrame(() => {
        setIsAnimating(true);
      });
    } else {
      document.body.style.overflow = 'unset';
      setIsVisible(false);
      setIsAnimating(false);
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedImage]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedImage) {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [selectedImage]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  if (!isVisible && !selectedImage) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const glowConfig = {
    venue: {
      color: '#44c4ff',
      shadow: '0 0 20px #44c4ff80, 0 0 40px #44c4ff30',
      border: 'border-[#44c4ff]',
    },
    benefit: {
      color: '#1ef99d',
      shadow: '0 0 20px #1ef99d80, 0 0 40px #1ef99d30',
      border: 'border-[#1ef99d]',
    },
    goods: {
      color: '#fcb122',
      shadow: '0 0 20px #fcb12280, 0 0 40px #fcb12230',
      border: 'border-[#fcb122]',
    },
  };

  const glow = glowConfig[imageType];

  return (
    <div 
      className={`fixed inset-0 z-[9999] flex items-center justify-center cursor-pointer transition-opacity duration-300 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}
      onClick={handleOverlayClick}
    >
      <div 
        className={`absolute inset-0 bg-black/70 transition-opacity duration-300 ${isAnimating ? 'opacity-100' : 'opacity-0'}`} 
        onClick={handleOverlayClick}
      ></div>

      <div 
        className={`relative z-10 flex items-center justify-center transition-all duration-300 ${isAnimating ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleClose}
          className="absolute -top-12 right-0 text-white hover:text-[#1ef99d] transition-colors text-3xl font-bold w-10 h-10 flex items-center justify-center cursor-pointer"
          aria-label="Close"
        >
          ×
        </button>

        <div
          className={`${imageType === 'venue' ? 'bg-white p-2' : 'p-0'} rounded-lg mx-4`}
          style={{ boxShadow: glow.shadow }}
        >
          <img
            src={selectedImage || ''}
            alt="Goods detail"
            className="max-w-[90vw] max-h-[90vh] w-auto h-auto object-contain cursor-default rounded-lg"
          />
        </div>
      </div>
    </div>
  );
}
